// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  validateToken as validateSessionToken,
  subscribeToSession,
  getSessionErrorMessage
} from '../services/sessionService';
import { IS_DEMO_MODE } from '../config/demoMode';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ─────────────────────────────────────────────────────────────────────────────
// LOCAL DEV / STATIC-DEMO BYPASS
// Skip Firebase entirely and inject a mock session so all data-dependent pages work with
// the golden demo client (client_id=200) WITHOUT a backend — on localhost AND in any static
// DEMO build (Cloudflare Pages). Without this, clientId is null off-localhost and the
// completion metrics / shared pages render empty. The real Firebase auth path is untouched
// for any non-demo production build.
//
// Keys off VITE_ENABLE_DEMO_MODE, NOT the enterprise flag: the demo client binding is needed
// by every demo build, storefront-only ones included. See src/config/demoMode.ts.
// ─────────────────────────────────────────────────────────────────────────────
const IS_LOCAL_DEV =
  window.location.hostname === 'localhost' ||
  IS_DEMO_MODE;

// displayName drives the header greeting ("Welcome back, {user.name}!" in AppHeader).
// It is 'Dana Whitfield' because the demo script introduces her by name and the pre-flight
// checklist expects "Welcome back, Dana" — 'Dev User' put a placeholder on the opening screen.
// Matches primary_contact.name in src/fixtures/acmeOrgProfile.
const DEV_MOCK_USER = {
  uid: 'dev-bypass-uid',
  email: 'dana.whitfield@acmehealth.example',
  displayName: 'Dana Whitfield',
  getIdToken: async () => 'dev-token',
};

const DEV_MOCK_SESSION = {
  client_id: '200',
  user_id: 'dev-bypass-uid',
  role: 'CLIENT_ADMIN',
  status: 'active',
  client_ref: null,
};

const DEV_MOCK_CLIENT = {
  id: '200',
  name: 'Sample Health System',
  client_id: '200',
  current_entitlement_ref: null,
};

export const AuthProvider = ({ children }) => {
  // Firebase Auth state
  const [currentUser, setCurrentUser] = useState(IS_LOCAL_DEV ? DEV_MOCK_USER : null);
  const [loading, setLoading] = useState(!IS_LOCAL_DEV);
  const [error, setError] = useState(null);

  // Session state
  const [session, setSession] = useState(IS_LOCAL_DEV ? DEV_MOCK_SESSION : null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState(null);

  // Client state
  const [client, setClient] = useState(IS_LOCAL_DEV ? DEV_MOCK_CLIENT : null);
  const [clientLoading, setClientLoading] = useState(false);

  // Entitlement state
  const [entitlement, setEntitlement] = useState(null);
  const [entitlementLoading, setEntitlementLoading] = useState(false);

  // Refs to store Firestore unsubscribe functions
  const sessionUnsubscribeRef = useRef(null);
  const clientUnsubscribeRef = useRef(null);
  const entitlementUnsubscribeRef = useRef(null);

  // Sign up with email and password
  const signup = async (email, password) => {
    try {
      setError(null);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Sign in with email and password
  const login = async (email, password) => {
    try {
      setError(null);
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Sign in with Google
  const loginWithGoogle = async () => {
    try {
      setError(null);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Validate token with backend API
  const validateToken = async (token, userId) => {
    try {
      setSessionError(null);
      setSessionLoading(true);
      const result = await validateSessionToken(token, userId);
      console.log('✓ Token validated successfully:', result);
      return result;
    } catch (err) {
      const friendlyError = getSessionErrorMessage(err);
      setSessionError(friendlyError);
      console.error('Session validation failed:', err);
      throw new Error(friendlyError);
    } finally {
      setSessionLoading(false);
    }
  };

  // Sign out
  const logout = async () => {
    try {
      setError(null);

      if (sessionUnsubscribeRef.current) {
        sessionUnsubscribeRef.current();
        sessionUnsubscribeRef.current = null;
      }
      if (clientUnsubscribeRef.current) {
        clientUnsubscribeRef.current();
        clientUnsubscribeRef.current = null;
      }
      if (entitlementUnsubscribeRef.current) {
        entitlementUnsubscribeRef.current();
        entitlementUnsubscribeRef.current = null;
      }

      setSession(null);
      setSessionError(null);
      setClient(null);
      setEntitlement(null);

      await firebaseSignOut(auth);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Listen to Firebase auth state changes — skipped on localhost
  useEffect(() => {
    if (IS_LOCAL_DEV) return;

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Subscribe to Firestore session document — skipped on localhost
  useEffect(() => {
    if (IS_LOCAL_DEV) return;

    if (!currentUser) {
      if (sessionUnsubscribeRef.current) {
        sessionUnsubscribeRef.current();
        sessionUnsubscribeRef.current = null;
      }
      setSession(null);
      setSessionError(null);
      return;
    }

    console.log('📡 Setting up session subscription for user:', currentUser.uid);

    const unsubscribe = subscribeToSession(
      currentUser.uid,
      (sessionData) => {
        console.log('📊 Session update received:', sessionData);
        setSession(sessionData);
        setSessionLoading(false);

        if (sessionData.exists && sessionData.status === 'revoked') {
          setSessionError('Your session has been revoked. Please sign in again.');
        }
      },
      (error) => {
        console.error('Session subscription error:', error);
        setSessionError('Failed to connect to session service.');
        setSessionLoading(false);
      }
    );

    sessionUnsubscribeRef.current = unsubscribe;

    return () => {
      if (sessionUnsubscribeRef.current) {
        sessionUnsubscribeRef.current();
        sessionUnsubscribeRef.current = null;
      }
    };
  }, [currentUser]);

  // Subscribe to Firestore client document — skipped on localhost
  useEffect(() => {
    if (IS_LOCAL_DEV) return;

    if (clientUnsubscribeRef.current) {
      clientUnsubscribeRef.current();
      clientUnsubscribeRef.current = null;
    }

    setClient(null);

    if (!session?.client_ref) {
      setClientLoading(false);
      return;
    }

    let clientRef = session.client_ref;

    if (clientRef?.referencePath && typeof clientRef.referencePath === 'string') {
      clientRef = doc(db, clientRef.referencePath);
    }

    console.log('📡 Setting up client subscription');
    setClientLoading(true);

    const unsubscribe = onSnapshot(
      clientRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          console.log('📊 Client update received:', docSnapshot.id);
          setClient({ ...docSnapshot.data(), id: docSnapshot.id });
        } else {
          console.warn('Client document does not exist');
          setClient(null);
        }
        setClientLoading(false);
      },
      (error) => {
        console.error('Client subscription error:', error);
        setClient(null);
        setClientLoading(false);
      }
    );

    clientUnsubscribeRef.current = unsubscribe;

    return () => {
      if (clientUnsubscribeRef.current) {
        clientUnsubscribeRef.current();
        clientUnsubscribeRef.current = null;
      }
    };
  }, [session?.client_ref]);

  // Subscribe to Firestore entitlement document — skipped on localhost
  useEffect(() => {
    if (IS_LOCAL_DEV) return;

    if (entitlementUnsubscribeRef.current) {
      entitlementUnsubscribeRef.current();
      entitlementUnsubscribeRef.current = null;
    }

    setEntitlement(null);

    if (!client?.current_entitlement_ref) {
      setEntitlementLoading(false);
      return;
    }

    let entitlementRef = client.current_entitlement_ref;

    if (entitlementRef?.referencePath && typeof entitlementRef.referencePath === 'string') {
      entitlementRef = doc(db, entitlementRef.referencePath);
    }

    console.log('📡 Setting up entitlement subscription');
    setEntitlementLoading(true);

    const unsubscribe = onSnapshot(
      entitlementRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          console.log('📊 Entitlement update received:', docSnapshot.id);
          setEntitlement({ ...docSnapshot.data(), id: docSnapshot.id });
        } else {
          console.warn('Entitlement document does not exist');
          setEntitlement(null);
        }
        setEntitlementLoading(false);
      },
      (error) => {
        console.error('Entitlement subscription error:', error);
        setEntitlement(null);
        setEntitlementLoading(false);
      }
    );

    entitlementUnsubscribeRef.current = unsubscribe;

    return () => {
      if (entitlementUnsubscribeRef.current) {
        entitlementUnsubscribeRef.current();
        entitlementUnsubscribeRef.current = null;
      }
    };
  }, [client?.current_entitlement_ref]);

  const value = {
    currentUser,
    login,
    signup,
    logout,
    resetPassword,
    loginWithGoogle,
    error,
    loading,
    // Session properties
    session,
    sessionLoading,
    sessionError,
    validateToken,
    // Client properties
    client,
    clientLoading,
    // Entitlement properties
    entitlement,
    entitlementLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};